import React, { useMemo, useState, type ReactNode } from "react";
import { Tab, Transition } from "@headlessui/react";
import type { ActivityViewData } from "./types";
import { Chip } from "../Chip";
import Button from "../Button";
import { TooltipContainer } from "../Tooltip.jsx";
import { twClassNames } from "../../helpers/twClassNames";

function countUnread(activities: ActivityViewData[]): number {
    return activities.filter((a) => !a.read).length;
}

const ALL_TAB_ID = "all";

export type ActivityTrayTab = { id: string; label: string; icon?: string };

import type { ActivityAction } from "./views/ActivityItemShell";

export type ActivityItemOptions = { onMarkRead?: () => void; action?: ActivityAction };

export type ActivityTrayProps = {
    activities: ActivityViewData[];
    isLoading: boolean;
    renderItem: (activity: ActivityViewData, itemOptions?: ActivityItemOptions) => ReactNode;
    title?: string;
    showHeader?: boolean;
    tabs?: ActivityTrayTab[];
    filterActivities?: (activities: ActivityViewData[], tabId: string) => ActivityViewData[];
    onMarkAllRead?: () => void;
    onMarkRead?: (activity: ActivityViewData) => void;
    getAction?: (activity: ActivityViewData) => ActivityAction | undefined;
    seeAllHref?: string | null;
    seeAllLabel?: string;
};

function ActivityList({
    items,
    isLoading,
    renderItem,
    onMarkRead,
    getAction
}: {
    items: ActivityViewData[];
    isLoading: boolean;
    renderItem: ActivityTrayProps["renderItem"];
    onMarkRead?: ActivityTrayProps["onMarkRead"];
    getAction?: ActivityTrayProps["getAction"];
}) {
    return (
        <>
            {isLoading && items.length === 0 && (
                <div className="flex items-center justify-center py-12 text-gray-400">
                    <i className="far fa-spinner-third fa-spin mr-2" />
                </div>
            )}
            {!isLoading && items.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <i className="far fa-check-circle mb-2 text-2xl" />
                    <p className="text-sm">No recent activity</p>
                </div>
            )}
            {items.map((activity) => (
                <Transition
                    key={activity.id}
                    appear
                    show
                    enter="transition-opacity duration-300 ease-out"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                >
                    {renderItem(activity, {
                        onMarkRead: onMarkRead ? () => onMarkRead(activity) : undefined,
                        action: getAction?.(activity)
                    })}
                </Transition>
            ))}
        </>
    );
}

export function ActivityTray({
    activities,
    isLoading,
    renderItem,
    title = "Activity",
    showHeader = true,
    tabs,
    filterActivities,
    onMarkAllRead,
    onMarkRead,
    getAction,
    seeAllHref,
    seeAllLabel = "See All Activity"
}: ActivityTrayProps) {
    const hasTabs = tabs != null && tabs.length > 0;
    const tabList = hasTabs ? [{ id: ALL_TAB_ID, label: "All" }, ...tabs] : [];
    const [selectedIndex, setSelectedIndex] = useState(0);

    const getTabCount = (tabId: string) =>
        tabId === ALL_TAB_ID
            ? activities.length
            : (filterActivities?.(activities, tabId)?.length ?? 0);

    const getActivitiesForTab = (tabId: string) =>
        !filterActivities ? activities : filterActivities(activities, tabId);

    const unreadCount = useMemo(() => countUnread(activities), [activities]);
    const showMarkAllRead = unreadCount > 0 && !!onMarkAllRead;

    return (
        <div className="flex h-full w-[stretch] flex-col bg-white">
            {showHeader && (
                <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 items-center justify-between border-b border-gray-100 bg-white px-4 py-3">
                    <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
                        <Chip
                            color="purple"
                            type="number"
                            className="h-5 min-w-5 justify-center px-1 py-0 leading-none"
                        >
                            {activities.length}
                        </Chip>
                    </div>
                    {showMarkAllRead && (
                        <TooltipContainer
                            type="titleOnly"
                            title="Mark all as read"
                            placement="top"
                            customButton={
                                <Button
                                    type="secondary"
                                    size="xxsmall"
                                    shape="square"
                                    onClick={() => onMarkAllRead?.()}
                                    aria-label="Mark all as read"
                                >
                                    <i className="far fa-list-check" />
                                </Button>
                            }
                        />
                    )}
                </div>
            )}

            {hasTabs ? (
                <div className="flex min-h-0 flex-1 flex-col">
                    <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                        <Tab.List className="flex flex-shrink-0 gap-1 border-b border-gray-100 px-2">
                            {tabList.map((tab) => (
                                <Tab
                                    key={tab.id}
                                    className={({ selected }) =>
                                        twClassNames(
                                            "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none",
                                            selected
                                                ? "border-brand text-brand"
                                                : "border-transparent text-gray-500 hover:text-gray-700"
                                        )
                                    }
                                >
                                    {({ selected }) => (
                                        <>
                                            {tab.icon && (
                                                <i
                                                    className={twClassNames(
                                                        "far",
                                                        tab.icon,
                                                        "text-xs"
                                                    )}
                                                    aria-hidden
                                                />
                                            )}
                                            {tab.label}
                                            <Chip
                                                color={selected ? "purple" : "gray"}
                                                type="number"
                                                className="h-5 min-w-5 justify-center px-1 py-0 leading-none"
                                            >
                                                {getTabCount(tab.id)}
                                            </Chip>
                                        </>
                                    )}
                                </Tab>
                            ))}
                        </Tab.List>
                        <Tab.Panels className="flex min-h-0 flex-1 flex-col">
                            {tabList.map((tab) => (
                                <Tab.Panel
                                    key={tab.id}
                                    className="flex min-h-0 flex-1 flex-col focus:outline-none"
                                >
                                    <div className="flex-1 divide-y divide-gray-100 overflow-y-auto">
                                        <ActivityList
                                            items={getActivitiesForTab(tab.id)}
                                            isLoading={isLoading}
                                            renderItem={renderItem}
                                            onMarkRead={onMarkRead}
                                            getAction={getAction}
                                        />
                                    </div>
                                </Tab.Panel>
                            ))}
                        </Tab.Panels>
                    </Tab.Group>
                </div>
            ) : (
                <div className="flex min-h-0 flex-1 flex-col divide-y divide-gray-100 overflow-y-auto">
                    <ActivityList
                        items={activities}
                        isLoading={isLoading}
                        renderItem={renderItem}
                        onMarkRead={onMarkRead}
                        getAction={getAction}
                    />
                </div>
            )}

            {seeAllHref && (
                <div className="flex flex-shrink-0 justify-center border-t border-gray-100 px-4 py-3">
                    <a
                        href={seeAllHref}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    >
                        {seeAllLabel}
                    </a>
                </div>
            )}
        </div>
    );
}
